/**
 * Version checking utility for detecting when a newer version is available
 */

// Current app version from package.json
const CURRENT_VERSION = '1.6.0';
const VERSION_CHECK_KEY = 'dfb_tournament_version_check';
const LAST_CHECK_KEY = 'dfb_tournament_last_version_check';

// Check interval (in milliseconds) - check every 30 minutes
const CHECK_INTERVAL = 30 * 60 * 1000;

/**
 * Compare two semantic version strings
 * @param {string} version1 - First version (e.g., "1.2.3")
 * @param {string} version2 - Second version (e.g., "1.2.4")
 * @returns {number} - Returns 1 if version1 > version2, -1 if version1 < version2, 0 if equal
 */
export const compareVersions = (version1, version2) => {
  const v1parts = version1.split('.').map(Number);
  const v2parts = version2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(v1parts.length, v2parts.length); i++) {
    const v1part = v1parts[i] || 0;
    const v2part = v2parts[i] || 0;
    
    if (v1part > v2part) return 1;
    if (v1part < v2part) return -1;
  }
  
  return 0;
};

/**
 * Check if we should perform a version check based on timing
 * @returns {boolean} - True if we should check for updates
 */
const shouldCheckForUpdates = () => {
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
  return timeSinceLastCheck > CHECK_INTERVAL;
};

/**
 * Fetch the latest version from the remote server
 * @param {string} baseUrl - Base URL of the application (optional)
 * @returns {Promise<string|null>} - Latest version string or null if check failed
 */
const fetchLatestVersion = async (baseUrl = '') => {
  try {
    // Try to fetch package.json from the server
    const response = await fetch(`${baseUrl}/package.json?_t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const packageData = await response.json();
    return packageData.version;
  } catch (error) {
    // Fallback: try to check if there's a newer build by checking a timestamp endpoint
    try {
      const buildResponse = await fetch(`${baseUrl}/build-timestamp.json?_t=${Date.now()}`, {
        cache: 'no-cache'
      });

      if (buildResponse.ok) {
        const buildData = await buildResponse.json();
        // If we have a newer build timestamp, suggest there might be an update
        const lastKnownTimestamp = localStorage.getItem('last_build_timestamp');
        if (lastKnownTimestamp && buildData.timestamp > parseInt(lastKnownTimestamp, 10)) {
          return 'newer'; // Indicate there's likely a newer version
        }
        localStorage.setItem('last_build_timestamp', buildData.timestamp.toString());
      }
    } catch (fallbackError) {
      // Silently fail - version check is not critical
    }

    return null;
  }
};

/**
 * Check for application updates
 * @param {function} onUpdateAvailable - Callback when update is available
 * @param {string} baseUrl - Base URL of the application (optional)
 * @returns {Promise<boolean>} - True if update is available
 */
export const checkForUpdates = async (onUpdateAvailable, baseUrl = '') => {
  // Don't check if we're offline
  if (!navigator.onLine) {
    return false;
  }

  // Don't check too frequently
  if (!shouldCheckForUpdates()) {
    return false;
  }

  try {
    const latestVersion = await fetchLatestVersion(baseUrl);
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());

    if (!latestVersion) {
      return false;
    }

    // Handle special case where we detected a newer build but don't have exact version
    if (latestVersion === 'newer') {
      if (onUpdateAvailable) {
        onUpdateAvailable(CURRENT_VERSION, 'newer');
      }
      return true;
    }

    // Compare versions
    const comparison = compareVersions(latestVersion, CURRENT_VERSION);
    if (comparison > 0) {
      localStorage.setItem(VERSION_CHECK_KEY, latestVersion);

      if (onUpdateAvailable) {
        onUpdateAvailable(CURRENT_VERSION, latestVersion);
      }
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // Silently fail - version check is not critical
    return false;
  }
};

/**
 * Get the current app version
 * @returns {string} - Current version string
 */
export const getCurrentVersion = () => CURRENT_VERSION;

/**
 * Clear version check cache (useful for testing)
 */
export const clearVersionCache = () => {
  localStorage.removeItem(VERSION_CHECK_KEY);
  localStorage.removeItem(LAST_CHECK_KEY);
  localStorage.removeItem('last_build_timestamp');
};

/**
 * Force reload the page to get the latest version
 */
export const reloadForUpdate = () => {
  // Clear cache and reload
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
      window.location.reload(true);
    });
  } else {
    window.location.reload(true);
  }
};
